import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { apiService } from "../../services/api";
import { Clock, AlertCircle, Settings } from "lucide-react";
import LoginFormInit from "./LoginFormInit";
import LoginFormStandard from "./LoginFormStandard";
import LoginFormSystem from "./LoginFormSystem";

type LoginMode = "standard" | "system" | "init";

// Translates a thrown axios error from a login attempt into a message the
// user can act on. Centralized so all three login flows show the same
// language for the same situation, and so the "wrong credentials vs.
// can't reach the server" distinction isn't accidentally collapsed.
//
// fallback is shown only when nothing else matches — typically an in-page
// validation we couldn't classify.
function interpretLoginError(error: any, fallback: string): string {
  // Server replied with a structured error — trust it. Backend already
  // distinguishes ACCOUNT_INACTIVE vs INVALID_CREDENTIALS at this point.
  const serverMessage = error?.response?.data?.error;
  if (serverMessage) return serverMessage;

  const status = error?.response?.status;
  if (status === 429) {
    return "Too many login attempts. Please wait a few minutes and try again.";
  }
  if (typeof status === "number" && status >= 500) {
    return "The server is having trouble. Please try again in a moment.";
  }

  // No response at all — network drop, DNS failure, CORS rejection, server
  // down. Axios sets error.code to "ERR_NETWORK" / "ECONNABORTED" /
  // "ETIMEDOUT" depending on the cause; either way error.response is
  // undefined.
  if (!error?.response) {
    return "Couldn't reach the server. Check your internet connection and try again.";
  }

  return fallback;
}

const Login: React.FC = () => {
  const { login } = useAuth();
  const [searchParams] = useSearchParams();
  const redirectPath = searchParams.get("redirect");
  const [mode, setMode] = useState<LoginMode>("standard");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [isSystemInitialized, setIsSystemInitialized] = useState<
    boolean | null
  >(null);


  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");

  useEffect(() => {
    checkSystemStatus();
  }, []);

  const checkSystemStatus = async () => {
    try {
      const status = await apiService.getSystemStatus();
      setIsSystemInitialized(status.isInitialized);
      if (!status.isInitialized) {
        setMode("init");
      }
    } catch (error) {
      console.error("Failed to check system status:", error);
      setError("Failed to connect to server");
    }
  };

  const loginAndRedirect = (userData: any, organizationData: any, token?: string) => {
    login(userData, organizationData, token);
    if (redirectPath) {
      // Use hard navigation to avoid race between auth state change and React Router's
      // catch-all redirect — ensures we land on the target path (e.g. /desktop-auth)
      window.location.href = redirectPath;
      return;
    }
  };

  const resetForm = () => {
    setUsername("");
    setPassword("");
    setName("");
    setEmail("");
    setError("");
  };

  const handleSystemInit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setIsLoading(true);
    try {
      if (!username.trim() || !password.trim() || !name.trim()) {
        setError("Username, password, and name are required");
        return;
      }
      const response = await apiService.initializeSystem({
        username: username.trim(),
        password: password.trim(),
        name: name.trim(),
        email: email.trim() || undefined,
      });
      setIsSystemInitialized(true);
      loginAndRedirect(response.user, response.organization, response.token);
    } catch (error: any) {
      console.error("System initialization error:", error);
      setError(interpretLoginError(error, "System initialization failed. Please try again."));
    } finally {
      setIsLoading(false);
    }
  };

  const handleStandardLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setIsLoading(true);
    try {
      if (!username.trim() || !password.trim()) {
        setError("Username and password are required");
        return;
      }
      const response = await apiService.login({
        username: username.trim(),
        password: password.trim(),
      });
      loginAndRedirect(response.user, response.organization, response.token);
    } catch (error: any) {
      console.error("Standard login error:", error);
      setError(interpretLoginError(error, "Login failed. Please try again."));
    } finally {
      setIsLoading(false);
    }
  };

  const handleSystemLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setIsLoading(true);
    try {
      if (!username.trim() || !password.trim()) {
        setError("Username and password are required");
        return;
      }
      const response = await apiService.login({
        username: username.trim(),
        password: password.trim(),
      });
      if (response.user.role !== "system_owner") {
        // Same generic phrasing as the server's response so the wrong-portal
        // case doesn't accidentally leak that the username exists as a
        // non-system-owner account.
        setError("Invalid username or password.");
        return;
      }
      loginAndRedirect(response.user, response.organization, response.token);
    } catch (error: any) {
      console.error("System login error:", error);
      setError(interpretLoginError(error, "System administrator login failed. Please try again."));
    } finally {
      setIsLoading(false);
    }
  };

  const getModeInfo = () => {
    switch (mode) {
      case "init":
        return {
          title: "System Initialization",
          description: "Set up the system administrator account",
          icon: <Settings className="w-5 h-5 text-white" />,
          iconBg: "bg-gray-800",
        };
      case "system":
        return {
          title: "System Administrator",
          description: "Access system administration",
          icon: <Settings className="w-5 h-5 text-white" />,
          iconBg: "bg-error-600",
        };
      default:
        return {
          title: "Welcome back",
          description: "Sign in to your organization account",
          icon: <Clock className="w-5 h-5 text-white" />,
          iconBg: "bg-primary-600",
        };
    }
  };

  const handleInitChange = (field: keyof import("./LoginFormInit").LoginFormInitValues, value: string) => {
    if (field === "username") setUsername(value);
    else if (field === "password") setPassword(value);
    else if (field === "name") setName(value);
    else if (field === "email") setEmail(value);
  };

  const handleStandardChange = (field: "username" | "password", value: string) => {
    if (field === "username") setUsername(value);
    else setPassword(value);
  };

  const handleSystemChange = (field: "username" | "password", value: string) => {
    if (field === "username") setUsername(value);
    else setPassword(value);
  };

  if (isSystemInitialized === null) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center p-4">
        <div className="w-full max-w-sm text-center animate-fade-in">
          <div className="w-10 h-10 bg-primary-600 rounded-xl flex items-center justify-center mx-auto mb-4">
            <Clock className="w-5 h-5 text-white" />
          </div>
          <div className="animate-spin rounded-full h-6 w-6 border-2 border-gray-200 border-t-primary-600 mx-auto mt-6"></div>
          <p className="mt-3 text-sm text-gray-400">Loading...</p>
        </div>
      </div>
    );
  }

  const { title, description, icon, iconBg } = getModeInfo();

  return (
    <div className="min-h-screen bg-white flex items-center justify-center p-4">
      <div className="w-full max-w-sm animate-slide-up">
        {/* Logo & Title */}
        <div className="text-center mb-8">
          <div className={`w-12 h-12 ${iconBg} rounded-2xl flex items-center justify-center mx-auto mb-5 shadow-elevated`}>
            {icon}
          </div>
          <h1 className="text-2xl font-display font-bold text-gray-900 tracking-tight">
            {title}
          </h1>
          <p className="text-sm text-gray-400 mt-1.5">{description}</p>
        </div>

        {error && (
          <div className="mb-5 p-3.5 bg-error-50 border border-error-100 rounded-xl flex items-start gap-2.5 animate-fade-in">
            <AlertCircle className="w-4 h-4 text-error-500 flex-shrink-0 mt-0.5" />
            <p className="text-error-700 text-sm leading-snug">{error}</p>
          </div>
        )}

        {mode === "init" && (
          <LoginFormInit
            values={{ username, password, name, email }}
            onChange={handleInitChange}
            onSubmit={handleSystemInit}
            isLoading={isLoading}
            error={error}
          />
        )}

        {mode === "standard" && (
          <LoginFormStandard
            values={{ username, password }}
            onChange={handleStandardChange}
            onSubmit={handleStandardLogin}
            onSwitchToSystem={() => {
              resetForm();
              setMode("system");
            }}
            isLoading={isLoading}
            error={error}
          />
        )}

        {mode === "system" && (
          <LoginFormSystem
            values={{ username, password }}
            onChange={handleSystemChange}
            onSubmit={handleSystemLogin}
            onSwitchToStandard={() => {
              resetForm();
              setMode("standard");
            }}
            isLoading={isLoading}
            error={error}
          />
        )}
      </div>
    </div>
  );
};

export default Login;
